import { z } from 'zod';
import { LINK_TYPES } from '@/types/entity';

/**
 * K5-d — 커널 제안의 도착 검증(신뢰 경계). 사이드카가 보낸 페이로드는 *외부 입력*이다:
 * 승인 큐에 들어가기 전에 형을 강제하고, 불량은 조용히 버리지 않고 반려 이벤트로 남긴다.
 */
const linkTypeSchema = z.enum(LINK_TYPES as [string, ...string[]]);

/** 제네릭 패치 — 엔티티 카드·관계·프로젝트 메타의 구체 수정 제안(수락 시 기존 store 액션으로 집행). */
export const proposalPatchSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('entity_field'), entity: z.string().min(1), field: z.string().min(1), value: z.union([z.string(), z.number(), z.array(z.string())]) }),
  z.object({ kind: z.literal('entity_name'), entity: z.string().min(1), name: z.string().min(1) }),
  z.object({ kind: z.literal('entity_summary'), entity: z.string().min(1), summary: z.string() }),
  z.object({ kind: z.literal('entity_tags'), entity: z.string().min(1), tags: z.array(z.string()) }),
  z.object({ kind: z.literal('add_link'), from: z.string().min(1), to: z.string().min(1), type: linkTypeSchema, label: z.string().optional(), directed: z.boolean().optional() }),
  z.object({ kind: z.literal('remove_link'), from: z.string().min(1), to: z.string().min(1), type: linkTypeSchema }),
  z.object({ kind: z.literal('project_meta'), key: z.string().min(1), value: z.string() }),
]);
export type ProposalPatchOp = z.infer<typeof proposalPatchSchema>;

/** 세계 사실/거시 Δ — applyProposalDelta가 집행하는 6종(K1·K5-c). */
export const proposalDeltaOpSchema = z.discriminatedUnion('op', [
  z.object({ op: z.literal('set_fact'), scope: z.string().min(1), fact: z.string().min(1) }),
  z.object({ op: z.literal('clear_fact'), scope: z.string().min(1), fact: z.string().min(1) }),
  z.object({ op: z.literal('rename_fact'), scope: z.string().min(1), from: z.string().min(1), to: z.string().min(1) }),
  z.object({ op: z.literal('collapse_resource'), resource: z.string().min(1), scope: z.string().min(1) }),
  z.object({ op: z.literal('uncollapse_resource'), resource: z.string().min(1), scope: z.string().min(1) }),
  z.object({ op: z.literal('set_institution_status'), institution: z.string().min(1), status: z.string().min(1) }),
]);

const rippleItemSchema = z.object({
  beat: z.string(),
  kind: z.string(),
  severity: z.string(),
  evidence: z.string(),
  canon_refs: z.array(z.string()).default([]),
});

const fixSchema = z.object({ beatId: z.string(), suggestion: z.string() });

const sceneSchema = z.object({ beatId: z.string().min(1), title: z.string().optional(), prose: z.string().min(1) });

/** 사이드카 'proposal' 이벤트의 전체 형 — 셋 중 하나는 실체가 있어야 한다(delta | scene | patches). */
export const incomingProposalSchema = z
  .object({
    kind: z.enum(['delta', 'scene', 'patch']).optional(),
    title: z.string().min(1),
    intent: z.string().optional(),
    delta: z.array(proposalDeltaOpSchema).optional(),
    ripple: z.array(rippleItemSchema).optional(),
    fixes: z.array(fixSchema).optional(),
    scene: sceneSchema.optional(),
    patches: z.array(proposalPatchSchema).optional(),
  })
  .refine((p) => (p.delta?.length ?? 0) > 0 || Boolean(p.scene) || (p.patches?.length ?? 0) > 0, {
    message: '제안에 실체가 없다(delta·scene·patches 모두 비어 있음)',
  });
export type IncomingProposal = z.infer<typeof incomingProposalSchema>;
